import { useLocation } from 'react-router-dom';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import {
    Sidebar as AppSidebar,
    SidebarAccount,
    SidebarList,
    SidebarListItem,
    SidebarNav,
    useAuthentication,
    useCurrentUser,
} from '@freelancelabs/components';
import {
    BellOutline,
    ChartBarOutline,
    FolderOutline,
    HouseOutline,
    LayersOutline,
    LogoutOutline,
    MemoOutline,
    PageOutline,
    PeopleOutline,
    SettingsOutline,
    UserCircleOutline,
} from '@freelancelabs/icons';

import { AppRoutes, DataIntercomTarget } from '../../constants';
import useViewerAttachment from '../../domains/shared/hooks/useViewerAttachment';
import { MyModulesDocument, ViewerDocument } from '../../graphql/__generated__/types';

// The module a route serves (by stable `PortalModule.code`), or `null` when it
// depends on none. Home has no module.
const MODULE_OF: { [route: string]: string | null } = {
    [AppRoutes.HOME]: null,
    [AppRoutes.DASHBOARD]: 'dashboard',
    [AppRoutes.NEEDS]: 'needs',
    [AppRoutes.SERVICES]: 'activity',
    [AppRoutes.PARTNERS]: 'partners',
    [AppRoutes.PURCHASE_ORDERS]: 'orders',
    [AppRoutes.INVOICES]: 'invoices',
};

const Sidebar = () => {
    const { logout } = useAuthentication();
    const { pathname } = useLocation();
    // Identity comes from the API (`viewer`), with the Cognito token as the fallback
    // available before the API responds. `Viewer` is already cached from elsewhere.
    const { email: emailDuJeton, firstName: prenomDuJeton, lastName: nomDuJeton } = useCurrentUser();
    const { data: viewerData } = useQuery(ViewerDocument);
    const viewer = viewerData?.viewer;

    const firstName = viewer?.firstName ?? prenomDuJeton;
    const lastName = viewer?.lastName ?? nomDuJeton;
    const email = viewer?.email ?? emailDuJeton;
    // The address is the last fallback: someone may have no first or last name at all.
    const displayName = [firstName, lastName].filter(Boolean).join(' ') || email || c('Sidebar').t`My account`;

    // The menu shows modules open on any of the person's accounts. This is not a
    // security boundary (hiding an entry does not close its URL); each page must still
    // write `MODULE_NOT_ENABLED`. `myModules` is a union over memberships, never an
    // intersection, so an entry says nothing of what a given account will show.
    const { data, loading, error } = useQuery(MyModulesDocument);
    const openModules = new Set((data?.myModules ?? []).map((module) => module.code));

    // Read only to know when to stay silent (see the note below).
    const attachment = useViewerAttachment();

    const entries = [
        {
            title: c('Sidebar').t`Home`,
            icon: <HouseOutline />,
            to: AppRoutes.HOME,
            active: pathname === AppRoutes.HOME,
            target: DataIntercomTarget.CTA_HOME,
        },
        {
            title: c('Sidebar').t`Dashboard`,
            icon: <ChartBarOutline />,
            to: AppRoutes.DASHBOARD,
            active: pathname.startsWith(AppRoutes.DASHBOARD),
            target: DataIntercomTarget.CTA_DASHBOARD,
        },
        {
            title: c('Sidebar').t`My needs`,
            icon: <MemoOutline />,
            to: AppRoutes.NEEDS,
            active: pathname.startsWith(AppRoutes.NEEDS),
            target: DataIntercomTarget.CTA_NEEDS,
        },
        {
            // Opens on the first tab: /activity only redirects.
            title: c('Sidebar').t`My activity`,
            icon: <LayersOutline />,
            to: AppRoutes.SERVICES,
            active: pathname.startsWith(AppRoutes.ACTIVITY),
            target: DataIntercomTarget.CTA_ACTIVITY,
        },
        {
            title: c('Sidebar').t`Partners`,
            icon: <PeopleOutline />,
            to: AppRoutes.PARTNERS,
            active: pathname.startsWith(AppRoutes.PARTNERS),
            target: DataIntercomTarget.CTA_PARTNERS,
        },
        {
            title: c('Sidebar').t`Purchase orders`,
            icon: <FolderOutline />,
            to: AppRoutes.PURCHASE_ORDERS,
            active: pathname.startsWith(AppRoutes.PURCHASE_ORDERS),
            target: DataIntercomTarget.CTA_PURCHASE_ORDERS,
        },
        {
            title: c('Sidebar').t`Invoices`,
            icon: <PageOutline />,
            to: AppRoutes.INVOICES,
            active: pathname.startsWith(AppRoutes.INVOICES),
            target: DataIntercomTarget.CTA_INVOICES,
        },
    ];

    // On error, keep every entry rather than shortening the menu: a missing entry would
    // read as "not allowed" when it only means the query failed. An empty list is a
    // valid answer (no module open anywhere) and is stated by the note below.
    const visible = entries.filter(({ to }) => {
        const code = MODULE_OF[to];
        if (code === null) {
            return true;
        }
        if (error) {
            return true;
        }
        return openModules.has(code);
    });

    return (
        <AppSidebar
            variant="portal"
            // Kept out of the scrolling `sidebar-content` (overflow: auto), which clipped
            // the account menu once it exceeded the rail width.
            footer={
                <>
                    <SidebarAccount name={displayName}>
                        {import.meta.env.PUBLIC_ACCOUNT_URL && (
                            <a href={`${import.meta.env.PUBLIC_ACCOUNT_URL}/account?from=client-hub`}>
                                <UserCircleOutline />
                                {c('Sidebar').t`My account`}
                            </a>
                        )}
                        <a href="#settings" aria-disabled="true" onClick={(event) => event.preventDefault()}>
                            <SettingsOutline />
                            {c('Sidebar').t`Settings`}
                            <span className="sidebar-account__soon">{c('Sidebar soon').t`Soon`}</span>
                        </a>
                        <a href="#notifications" aria-disabled="true" onClick={(event) => event.preventDefault()}>
                            <BellOutline />
                            {c('Sidebar').t`Notifications`}
                            <span className="sidebar-account__soon">{c('Sidebar soon').t`Soon`}</span>
                        </a>
                        <hr />
                        <a
                            href="#logout"
                            className="sidebar-account__logout"
                            onClick={(event) => {
                                event.preventDefault();
                                void logout();
                            }}
                        >
                            <LogoutOutline />
                            {c('Sidebar').t`Logout`}
                        </a>
                    </SidebarAccount>
                </>
            }
        >
            <SidebarNav>
                <SidebarList>
                    {visible.map(({ title, icon, to, active, target }) => (
                        <SidebarListItem
                            key={to}
                            active={active}
                            title={title}
                            icon={icon}
                            activeIcon={icon}
                            to={to}
                            tooltipProps={{ 'data-intercom-target': target }}
                        />
                    ))}

                    {/* This note assumes at least one account, so it is silenced only when
                        we know there is none; Home speaks for that case instead. */}
                    {!error && visible.length === 1 && attachment.status !== 'unattached' && (
                        <li className="sidebar-list__note" role="status">
                            {loading
                                ? c('Sidebar').t`Loading your modules…`
                                : c('Sidebar')
                                      .t`No module is open on your accounts yet. Ask your Freelance.com contact to open the ones you need.`}
                        </li>
                    )}
                </SidebarList>
            </SidebarNav>
        </AppSidebar>
    );
};

export default Sidebar;
